import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Folder, ChevronRight, RefreshCw } from 'lucide-react'
import { api } from '../../lib/api.js'
import { fmtDay } from './lib.js'

// The links are cdmfiles:, which CDM App Updater opens in Finder on the
// studio Macs; a phone or iPad has nothing to open them with, so there the
// folder is shown as a path instead of a dead link. (iPadOS reports itself
// as a Mac, hence the touch-point check.)
export const canOpenFinder = () => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return false
  return !(/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

/**
 * "Session files": this session's folder on the Current Projects share,
 * found by the server from the date in New Session's folder names
 * (services/session-files.js). Renders nothing while loading or when the
 * server isn't set up to look — the rest of the page never waits on it.
 */
export default function SessionFiles({ sessionId, date }) {
  const key = ['sl-files', sessionId]
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api(`/log/sessions/${sessionId}/files`),
    staleTime: 5 * 60_000,
  })
  if (!data?.configured) return null
  const finder = canOpenFinder()

  return (
    <section>
      <FilesHeader title="Session files" queryKey={key} url={`/log/sessions/${sessionId}/files`} />
      {data.pending && <StillChecking />}
      {data.match === 'none' ? (
        <p className="text-xs text-faint">
          {data.error || `No folder dated ${fmtDay(date)} on Current Projects — it may have been saved somewhere else.`}
        </p>
      ) : (
        <>
          {data.match === 'possible' && (
            <p className="mb-2 text-xs text-muted">No folder clearly matches. These were saved the same day:</p>
          )}
          <div className="card divide-y divide-hairline">
            {data.folders.map((f) => (
              <FolderRow key={f.path} folder={f} finder={finder} />
            ))}
          </div>
          <p className="mt-1.5 text-xs text-faint">
            {finder
              ? 'Opens in Finder. The first time, Safari asks to open “CDM App Updater” — that’s what opens it.'
              : 'Opens in Finder on the studio Macs.'}
          </p>
        </>
      )}
    </section>
  )
}

/**
 * Section heading with a Refresh tap (Charles, 2026-10-02: he renamed a
 * project's folder and the page kept linking to the old name). The server
 * keeps an index of the share for 10 minutes and this device keeps the
 * answer for 5, so a rename could take a quarter of an hour to show; the
 * tap asks for a real re-read (`?fresh=1`) and swaps the answer in place —
 * the same two-layer refresh as the Calendar and the suggestions list.
 */
export function FilesHeader({ title, queryKey, url }) {
  const qc = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  async function refresh() {
    setRefreshing(true)
    try {
      qc.setQueryData(queryKey, await api(`${url}?fresh=1`))
    } catch {
      // keep what we have; the row stays as it was
    } finally {
      setRefreshing(false)
    }
  }
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h2 className="section-header">{title}</h2>
      <button
        type="button"
        onClick={refresh}
        disabled={refreshing}
        className="tap flex items-center gap-1 text-xs font-medium text-ink underline underline-offset-2 disabled:opacity-50"
      >
        <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
        {refreshing ? 'Checking…' : 'Refresh'}
      </button>
    </div>
  )
}

// The server's re-read of the share hadn't finished in time, so what is
// shown may still be the old names.
export function StillChecking() {
  return <p className="mb-2 text-xs text-muted">The server is still re-reading the share — try Refresh again in a moment.</p>
}

export function FolderRow({ folder, finder, detail }) {
  const where = [[folder.category, ...folder.parents].join(' › '), detail].filter(Boolean).join(' · ')
  const body = (
    <>
      <Folder size={18} className="shrink-0 text-navy" />
      <span className="min-w-0 flex-1">
        <span className="block break-words text-sm font-medium text-ink">{folder.name}</span>
        <span className="mt-0.5 block break-words text-xs text-muted">{where}</span>
      </span>
      {finder && <ChevronRight size={16} className="shrink-0 text-faint" />}
    </>
  )
  return (
    <div>
      {finder ? (
        <a href={folder.url} className="tap flex items-center gap-3 px-4 py-2.5 active:bg-grouped">
          {body}
        </a>
      ) : (
        <div className="flex items-center gap-3 px-4 py-2.5">{body}</div>
      )}
      {folder.home && <HomeNote home={folder.home} finder={finder} />}
    </div>
  )
}

/**
 * Red, by Charles's request (2026-09-29): the folder sits outside the
 * folder that holds the rest of its kind — probably saved in the wrong
 * place. A guess from names alone (services/session-files.js homeFor), so
 * it says "may", and names where the others are so it can be checked.
 */
function HomeNote({ home, finder, what = 'it', under = true }) {
  const where = home.path.split('/').join(' › ')
  return (
    <p className={under ? '-mt-1 px-4 pb-2.5 pl-[46px] text-xs text-danger' : 'mt-1.5 text-xs text-danger'}>
      May be in the wrong place — {home.sessions} other session{home.sessions === 1 ? '' : 's'} like {what}{' '}
      {home.sessions === 1 ? 'is' : 'are'} in{' '}
      {finder ? (
        <a href={home.url} className="font-medium underline underline-offset-2">
          {where}
        </a>
      ) : (
        <span className="font-medium">{where}</span>
      )}
      .
    </p>
  )
}

/**
 * "Project files" on a multi-day project's page (Charles, 2026-10-01): the
 * master folder that holds the days, found by the server from the days'
 * own folders (services/session-files.js rankProjectFolder). `named` says
 * the folder carries the project's name; without it the days were found
 * together in some folder (a client's, or one SMB shows with a mangled
 * name), which is worth a link but gets more careful wording. Renders
 * nothing while loading, when the server isn't set up to look, or before
 * the project has a day.
 */
export function ProjectFiles({ projectId, dayCount }) {
  const key = ['sl-project-files', projectId, dayCount]
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => api(`/log/projects/${projectId}/files`),
    staleTime: 5 * 60_000,
    enabled: dayCount > 0,
  })
  if (!dayCount || !data?.configured) return null
  const finder = canOpenFinder()
  const days = (n) => `${n} day${n === 1 ? '' : 's'}`

  let intro = null
  if (data.match === 'possible') {
    if (data.daysFound === 0) intro = 'None of the days’ folders was found. This folder carries the project’s name:'
    else if (data.onlyDay) intro = `Only ${data.onlyDay}’s folder was found. It is in:`
    else intro = 'No folder clearly matches. The days seem to be in:'
  } else if (data.match === 'sure' && !data.named) {
    intro = 'No folder is named for the project — its days are saved together in:'
  }

  return (
    <section>
      <FilesHeader title="Project files" queryKey={key} url={`/log/projects/${projectId}/files`} />
      {data.pending && <StillChecking />}
      {data.match === 'none' ? (
        <>
          <p className="text-xs text-faint">
            {data.error ||
              (data.loose
                ? `No project folder — the days are saved straight in ${data.loose} on Current Projects.`
                : 'No project folder found on Current Projects — it may be named differently, or not made yet.')}
          </p>
          {data.home && <HomeNote home={data.home} finder={finder} what="them" under={false} />}
        </>
      ) : (
        <>
          {intro && <p className="mb-2 text-xs text-muted">{intro}</p>}
          <div className="card divide-y divide-hairline">
            {data.folders.map((f) => (
              <FolderRow
                key={f.path}
                folder={f}
                finder={finder}
                detail={f.days > 0 ? `${days(f.days)} of ${dayCount} inside` : f.sessions > 0 ? `${f.sessions} session folder${f.sessions === 1 ? '' : 's'} inside` : null}
              />
            ))}
          </div>
          <p className="mt-1.5 text-xs text-faint">
            {finder
              ? 'Opens in Finder. The first time, Safari asks to open “CDM App Updater” — that’s what opens it.'
              : 'Opens in Finder on the studio Macs.'}
          </p>
        </>
      )}
    </section>
  )
}
